import { BaseDto } from '../../../api/scheduler/models/base-dto';
import { BaseShiftDto } from '../../../api/scheduler/models/base-shift-dto';
import { BaseStatus as ApiBaseStatus } from '../../../api/scheduler/models/base-status';
import { CreateBaseShiftRequest } from '../../../api/scheduler/models/create-base-shift-request';
import { ShiftOptionDto } from '../../../api/scheduler/models/shift-option-dto';
import { SaveBaseRequest } from '../../../api/scheduler/models/save-base-request';
import { UpdateBaseShiftItem } from '../../../api/scheduler/models/update-base-shift-item';
import { UpdateBaseShiftsRequest } from '../../../api/scheduler/models/update-base-shifts-request';
import {
  BaseDetail,
  BaseFormValue,
  BaseRow,
  BaseListShift,
  BaseShiftOption,
  BaseStatus,
  ShiftConfig,
  ShiftDraft,
} from '../models';

/** Green shown for a Base when the API sends no colour. */
export const DEFAULT_BASE_COLOR = '#72a82d';

/** The two shifts every Base has. A Base-specific shift carries an 8-character hex code instead. */
const FIXED_SHIFT_ORDER = ['DAY', 'NIGHT'];

const FIXED_SHIFT_NOTES: Record<string, string> = {
  DAY: 'Regular daytime operation shift',
  NIGHT: 'Regular night time operation shift',
};

/**
 * Short code of a Base-specific shift on the list chip: the first character of its name in
 * capitals, the same single character the Base details page shows for it.
 */
export function shiftShortCode(name: string): string {
  return (name.trim().charAt(0) || '?').toUpperCase();
}

function isStandByName(name: string): boolean {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .includes('standby');
}

/**
 * The list shows Day and Night, then at most one more chip: the Base's StandBy shift when it has
 * one, otherwise its newest Base-specific shift.
 */
function toListShifts(shifts: BaseShiftDto[]): BaseListShift[] {
  const fixed = shifts.flatMap((shift): BaseListShift[] => {
    if (shift.code === 'DAY') return [{ code: 'D', label: 'D', active: shift.active }];
    if (shift.code === 'NIGHT') return [{ code: 'N', label: 'N', active: shift.active }];
    return [];
  });
  const custom = sortedShifts(shifts).filter(isCustomShift);
  const extra = custom.find((shift) => isStandByName(shift.name)) ?? custom[0];
  return extra
    ? [...fixed, { code: 'S', label: shiftShortCode(extra.name), active: extra.active }]
    : fixed;
}

export function toBaseStatus(status: ApiBaseStatus): BaseStatus {
  return status === 'ACTIVE' ? 'Active' : 'Inactive';
}

export function toBaseRow(base: BaseDto): BaseRow {
  return {
    id: base.id,
    code: base.code,
    displayCode: base.displayCode?.trim() || base.code,
    colorCode: base.colorCode?.trim() || DEFAULT_BASE_COLOR,
    name: base.name,
    status: toBaseStatus(base.status),
    lead: base.baseLead?.fullName ?? '',
    leadUserId: base.baseLead?.userId ?? 0,
    leadActive: base.baseLead?.active ?? true,
    location: locationLabel(base.location),
    shifts: toListShifts(base.shifts),
    timeZone: base.timeZone ?? '',
    phone: base.contactNumber ?? '',
    email: base.email ?? '',
    remarks: base.remarks ?? '',
    profileImageUrl: base.profileImageUrl ?? null,
  };
}

export function toBaseDetail(base: BaseDto): BaseDetail {
  const row = toBaseRow(base);
  const shiftConfigs = sortedShifts(base.shifts).map(toShiftConfig);
  return {
    ...row,
    leadInitials: row.lead
      .split(/\s+/)
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase(),
    averageShiftDuration: averageDurationLabel(base.shifts),
    shiftConfigs,
  };
}

export function toFormValue(base: BaseRow): BaseFormValue {
  return {
    code: base.code,
    displayCode: base.displayCode,
    name: base.name,
    colorCode: base.colorCode,
    leadUserId: base.leadUserId ? String(base.leadUserId) : '',
    location: base.location,
    timeZone: base.timeZone,
    phone: base.phone,
    email: base.email,
    status: base.status,
    remarks: base.remarks,
  };
}

export function toSaveBaseRequest(value: BaseFormValue): SaveBaseRequest {
  return {
    code: value.code,
    displayCode: value.displayCode.trim() || null,
    name: value.name,
    colorCode: value.colorCode.toUpperCase(),
    baseLeadUserId: value.leadUserId ? Number(value.leadUserId) : null,
    location: value.location.trim() || null,
    timeZone: value.timeZone,
    contactNumber: value.phone || null,
    email: value.email || null,
    status: value.status === 'Active' ? 'ACTIVE' : 'INACTIVE',
    remarks: value.remarks || null,
  };
}

/**
 * Request for saving one edited shift. Day and Night are always listed (the API requires both and
 * rejects a rename); a Base-specific shift is listed only when it is the one being edited, so
 * shifts nobody touched stay as the server has them.
 */
export function toUpdateShiftsRequest(
  saved: ShiftConfig[],
  edited: ShiftConfig
): UpdateBaseShiftsRequest {
  const fixed = saved
    .filter((shift) => !shift.custom)
    .map((shift) => (shift.code === edited.code ? edited : shift));
  return { shifts: [...fixed, ...(edited.custom ? [edited] : [])].map(toUpdateShiftItem) };
}

export function toCreateShiftRequest(draft: ShiftDraft): CreateBaseShiftRequest {
  return {
    name: draft.name.trim(),
    active: draft.active,
    // Times are optional for a Base-specific shift, but sent together or not at all.
    ...(draft.start && draft.end ? { startTime: draft.start, endTime: draft.end } : {}),
  };
}

function toUpdateShiftItem(shift: ShiftConfig): UpdateBaseShiftItem {
  return {
    code: shift.code,
    ...(shift.custom ? { name: shift.name } : {}),
    // Day and Night always carry both times; a Base-specific shift's are optional, so it may clear them.
    startTime: shift.start || null,
    endTime: shift.end || null,
    active: shift.active,
  };
}

/** Location is free text. Older responses still send `{ id, name }`. */
function locationLabel(location: unknown): string {
  if (typeof location === 'string') return location;
  if (location && typeof location === 'object' && 'name' in location) {
    const name = (location as { name?: unknown }).name;
    return typeof name === 'string' ? name : '';
  }
  return '';
}

function isCustomShift(shift: BaseShiftDto): boolean {
  return shift.custom ?? !FIXED_SHIFT_ORDER.includes(shift.code);
}

function toShiftConfig(shift: BaseShiftDto): ShiftConfig {
  const custom = isCustomShift(shift);
  return {
    code: shift.code,
    name: shift.name,
    label: shift.displayName || shift.name,
    start: shift.startTime ?? '',
    end: shift.endTime ?? '',
    duration: shift.durationMinutes == null ? '—' : durationLabel(shift.durationMinutes),
    note: custom ? customShiftNote(shift.name) : (FIXED_SHIFT_NOTES[shift.code] ?? ''),
    active: shift.active,
    custom,
  };
}

/** "Standby Shift" reads as "Standby operational shift"; a name without "Shift" gets it appended. */
function customShiftNote(name: string): string {
  const trimmed = name.trim();
  const topic = trimmed.replace(/\s+shift$/i, '') || trimmed;
  return `${topic} operational shift`;
}

/** Base-specific shifts first, newest first, then the fixed Day and Night. */
function sortedShifts(shifts: BaseShiftDto[]): BaseShiftDto[] {
  const custom = shifts
    .filter(isCustomShift)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt) || b.id - a.id);
  const fixed = FIXED_SHIFT_ORDER.flatMap((code) => shifts.filter((shift) => shift.code === code));
  return [...custom, ...fixed];
}

function durationLabel(minutes: number): string {
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} ${hours === 1 ? 'hour' : 'hours'}`;
}

function averageDurationLabel(shifts: BaseShiftDto[]): string {
  const active = shifts.filter((shift) => shift.active && shift.durationMinutes != null);
  if (!active.length) return '—';
  const average =
    active.reduce((sum, shift) => sum + (shift.durationMinutes ?? 0), 0) / active.length / 60;
  return `${Number.isInteger(average) ? average : average.toFixed(1)} hrs`;
}

export function toBaseShiftOption(dto: ShiftOptionDto): BaseShiftOption {
  return {
    id: dto.id,
    name: dto.name,
    baseCode: dto.baseCode,
    baseName: dto.baseName,
    custom: dto.custom,
  };
}
